import React from "react";
import { motion } from "framer-motion";
import { Zap, Database, Workflow, Lock } from "lucide-react";

const metrics = [
  { icon: Zap,      value: "10×",         valueSize: "text-5xl sm:text-6xl", label: "Faster onboarding",    caption: "Hours, not weeks.",  bg: "#FFFAF0", glow: "rgba(255,200,70,0.45)",  shadow: "0 24px 60px -12px rgba(240,175,40,0.38)",  border: "#E2C96A" },
  { icon: Database, value: "100%",        valueSize: "text-5xl sm:text-6xl", label: "Centralized records",  caption: "One workspace.",     bg: "#F0FFF0", glow: "rgba(72,200,100,0.42)",   shadow: "0 24px 60px -12px rgba(50,180,80,0.36)",   border: "#7ACCA0" },
  { icon: Workflow, value: "Real-time",   valueSize: "text-3xl sm:text-4xl", label: "Compliance workflows", caption: "Always-on review.",  bg: "#FFFFF0", glow: "rgba(210,205,55,0.42)",   shadow: "0 24px 60px -12px rgba(195,185,35,0.36)",  border: "#CDCA6A" },
  { icon: Lock,     value: "Multi-tenant",valueSize: "text-3xl sm:text-4xl", label: "Level data isolation", caption: "Tenant-secured.",    bg: "#F5FFFA", glow: "rgba(50,195,175,0.42)",   shadow: "0 24px 60px -12px rgba(35,180,160,0.36)",  border: "#6ECDC0" },
];

export default function TrustedOps() {
  return (
    <section data-testid="trusted-ops-section" className="relative py-16 sm:py-24 lg:py-36 border-t border-[#0E1A1A]">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-3"
          data-testid="trusted-label"
        >
          Built for growing organizations
        </motion.div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.03em] leading-[1.05] font-medium max-w-2xl"
          >
            Operations teams ship faster on nexus-vs.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-[#9BB8B8] max-w-md text-base"
          >
            Four pillars that quietly remove the operational tax from contractor management.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {metrics.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -5, boxShadow: m.shadow, borderColor: m.border, transition: { duration: 0.28, ease: "easeOut" } }}
              data-testid={`trusted-metric-${i}`}
              className="group rounded-2xl p-6 border border-[#E8E8E0] relative overflow-hidden cursor-default"
              style={{ background: m.bg }}
            >
              {/* Per-card tinted glow orb */}
              <div
                className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(circle, ${m.glow}, transparent 70%)` }}
              />
              {/* Diagonal shimmer sweep */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                <div className="absolute top-0 h-full w-[55%] -left-[60%] bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 group-hover:translate-x-[340%] transition-transform duration-700 ease-out" />
              </div>
              <div className="relative">
                <m.icon className="w-5 h-5 text-[#6B7280] group-hover:text-[#FF4F00] transition-colors duration-300 mb-8" />
                <div className={`font-display ${m.valueSize} font-medium tracking-[-0.03em] text-[#0A0A0B] mb-2`}>{m.value}</div>
                <div className="text-sm text-[#1A1A1A] font-medium mb-1">{m.label}</div>
                <div className="text-xs text-[#52525B]">{m.caption}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
